import type { ScanEvent, ScanFlag } from '@/types/manifest';
import { formatToESTShort } from '@/lib/utils/date-format';

interface ScanActivityTimelineProps {
  events: ScanEvent[];
  maxItems?: number;
}

const flagStyles: Record<ScanFlag, string> = {
  None: 'bg-green-500',
  Duplicate: 'bg-yellow-500',
  Unmanifested: 'bg-red-500',
  Manual: 'bg-purple-500',
};

export function ScanActivityTimeline({ events, maxItems = 20 }: ScanActivityTimelineProps) {
  const displayEvents = events.slice(0, maxItems);

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-4 border-b border-gray-700">
        <h3 className="text-lg font-semibold">Scan Activity</h3>
        <p className="text-sm text-gray-400">Recent scan events</p>
      </div>

      <div className="p-4">
        {displayEvents.length === 0 ? (
          <p className="text-center text-gray-400 py-4">No scan activity yet</p>
        ) : (
          <div className="space-y-4">
            {displayEvents.map((event) => (
              <div key={event.id} className="flex items-start gap-3">
                <div
                  className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${flagStyles[event.flag]}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm">{event.barcodeValue}</span>
                    {event.flag !== 'None' && (
                      <span className="text-xs text-yellow-400">({event.flag})</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-400">
                    {event.operatorName} &middot; {formatToESTShort(event.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {events.length > maxItems && (
          <p className="mt-4 text-center text-sm text-gray-400">
            Showing {maxItems} of {events.length} events
          </p>
        )}
      </div>
    </div>
  );
}
